import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateLayout from '../../components/CandidateLayout';
import { jobsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Search, 
  MapPin, 
  Briefcase, 
  Building2, 
  Calendar,
  Loader2,
  Star
} from 'lucide-react';
import { toast } from 'sonner';

export default function CandidateJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await jobsAPI.getPublic();
      setJobs(response.data);
    } catch (error) {
      toast.error('Erro ao carregar vagas');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (jobId) => {
    setApplying(jobId);
    try {
      await jobsAPI.apply(jobId);
      toast.success('Candidatura enviada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao se candidatar');
    } finally {
      setApplying(null);
    }
  };

  const filteredJobs = jobs.filter(job => 
    job.titulo.toLowerCase().includes(search.toLowerCase()) ||
    job.descricao.toLowerCase().includes(search.toLowerCase()) ||
    job.localizacao.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <CandidateLayout>
      <div className="space-y-6" data-testid="candidate-jobs">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vagas Disponíveis</h1>
          <p className="text-slate-600">Encontre sua próxima oportunidade</p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Buscar por cargo, empresa ou localização..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-12 h-12"
            data-testid="search-jobs-input"
          />
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-16 text-center">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Nenhuma vaga encontrada
              </h3>
              <p className="text-slate-600">
                {search ? 'Tente ajustar sua busca' : 'Novas vagas serão publicadas em breve'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <Card 
                key={job.id} 
                className="border-slate-200 hover:shadow-md transition-shadow"
                data-testid={`job-card-${job.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {job.destaque && (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                            <Star className="w-3 h-3 mr-1" />
                            Destaque
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-slate-600">
                          {job.tipo_contrato}
                        </Badge>
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">
                        {job.titulo}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-3">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {job.company_name || 'Empresa'}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.localizacao}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(job.created_at)}
                        </span>
                      </div>
                      <p className="text-slate-600 line-clamp-2">
                        {job.descricao}
                      </p>
                    </div>
                    <Button 
                      className="ml-4 bg-cyan-600 hover:bg-cyan-700"
                      onClick={() => handleApply(job.id)}
                      disabled={applying === job.id}
                      data-testid={`apply-btn-${job.id}`}
                    >
                      {applying === job.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Candidatar'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </CandidateLayout>
  );
}
